import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { clearPersistedCart } from "@/hooks/useCartPersistence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Migración Ronda B.2: la sesión vive EXCLUSIVAMENTE en cookies httpOnly.
// - `access_token`: cookie de sesión regular (Secure, SameSite=Lax, HttpOnly)
// - `sa_token`:     cookie de super_admin al impersonar. Solo la lee el backend.
// El frontend NUNCA guarda JWT en localStorage.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Claves obsoletas de localStorage que arrastramos de la era pre-cookie.
// Se limpian una única vez al arrancar la app para no dejar tokens
// residuales en el navegador de los usuarios existentes.
const LEGACY_LOCAL_STORAGE_KEYS = [
  "token",
  "super_admin_token",
];

// Datos por tienda que también hay que limpiar al hacer logout o volver
// desde una impersonación, para no filtrar estado entre sesiones.
const PER_STORE_LOCAL_KEYS = [
  "company_logo",
  "auto_print_enabled",
  "quick_scan_mode",
  "auto_print_on_payment",
];

function purgeLegacyTokens() {
  LEGACY_LOCAL_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Limpieza única: cualquier token viejo en localStorage ya no vale.
    purgeLegacyTokens();
    verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifySession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // El userId no es información sensible (es un UUID) y varios hooks de
      // persistencia del carrito lo necesitan para aislar el estado por
      // usuario. Se guarda aparte del JWT, que sigue viviendo en cookie.
      if (response.data?.id) {
        localStorage.setItem("user_id", response.data.id);
      }
    } catch (error) {
      // 401 esperado si no hay cookie de sesión. El interceptor global
      // gestiona 401 durante navegación normal; aquí solo dejamos user=null.
      setUser(null);
      localStorage.removeItem("user_id");
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    // El backend setea la cookie httpOnly `access_token`. No tocamos
    // localStorage para el JWT, pero sí guardamos el userId público.
    const response = await axios.post(`${API}/auth/login`, { username, password }, {
      headers: { "X-Login-Username": username }
    });
    const { user: userData } = response.data;
    setUser(userData);
    if (userData?.id) {
      localStorage.setItem("user_id", userData.id);
    }
    return userData;
  };

  const register = async (email, password, storeName = null) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      store_name: storeName,
    });
    const { user: userData } = response.data;
    setUser(userData);
    if (userData?.id) {
      localStorage.setItem("user_id", userData.id);
    }
    return userData;
  };

  const logout = async () => {
    // Fire-and-forget: el backend borra `access_token` y `sa_token`.
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_) {
      // ignorar — el logout local sigue adelante aunque el backend falle
    }
    PER_STORE_LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem("user_id");
    purgeLegacyTokens();
    clearPersistedCart();
    setUser(null);
  };

  const returnToSuperAdmin = async () => {
    // Restaura la sesión del super_admin desde la cookie `sa_token`.
    try {
      await axios.post(`${API}/auth/stop-impersonation`);
      PER_STORE_LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
      // Recargar para forzar que todos los contextos (Settings, Offline, etc.)
      // vuelvan a bootstrappear con el rol restaurado.
      window.location.href = "/tiendas";
    } catch (err) {
      // Si sa_token expiró, forzar logout completo para evitar estados zombi.
      console.error("[returnToSuperAdmin] failed:", err?.response?.status);
      await logout();
      window.location.href = "/login";
    }
  };

  // `is_impersonating` lo emite el backend en /auth/me leyendo la cookie
  // `sa_token`. El frontend jamás la puede leer directamente (httpOnly).
  const isImpersonating = !!user?.is_impersonating;

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
  };

  /**
   * Save which sidebar items the current user wants HIDDEN.
   * Purely cosmetic — the role filter still governs actual access.
   * Optimistic: update state first, then PUT. If the request fails we
   * roll back and surface a toast at the call-site.
   */
  const updateMenuPreferences = async (hiddenMenuItems) => {
    const clean = Array.from(new Set((hiddenMenuItems || []).filter((x) => x && x !== "/")));
    const prevHidden = user?.hidden_menu_items || [];
    setUser((prev) => (prev ? { ...prev, hidden_menu_items: clean } : prev));
    try {
      const r = await axios.put(`${API}/auth/menu-preferences`, {
        hidden_menu_items: clean,
      });
      // Backend returns the canonical (deduped, filtered) list — use it.
      if (Array.isArray(r?.data?.hidden_menu_items)) {
        setUser((prev) => (prev ? { ...prev, hidden_menu_items: r.data.hidden_menu_items } : prev));
      }
      return true;
    } catch (err) {
      // Roll back on failure.
      setUser((prev) => (prev ? { ...prev, hidden_menu_items: prevHidden } : prev));
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
        updateMenuPreferences,
        returnToSuperAdmin,
        isImpersonating,
        // `token` se mantiene en el contract del provider para no romper
        // consumidores, pero SIEMPRE es null en el flujo cookie-only.
        token: null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
